import { useRef, useEffect } from 'react';

import { IconComponent } from '../../../../util/icons';
import vendorFeedPhoto from '../../../../asset/images/vendor-feed-photo.svg';
import vendorSyncProperty from '../../../../asset/images/vendor-sync-property.png';
import rentvineStep2 from '../../../../asset/images/rentvine-step-2.png';
import rentvineStep3 from '../../../../asset/images/rentvine-step-3.png';
import rentvineStep5 from '../../../../asset/images/rentvine-step-5.png';
import rentvineStep6 from '../../../../asset/images/rentvine-step-6.png';
import rentvineStep7 from '../../../../asset/images/rentvine-step-7.png';
import rentvineStep8 from '../../../../asset/images/rentvine-step-8.png';
import rentvineStep9 from '../../../../asset/images/rentvine-step-9.png';
import rentvineStep10 from '../../../../asset/images/rentvine-step-10.png';
import rentvineStep11 from '../../../../asset/images/rentvine-step-11.png';
import rentvineStep12 from '../../../../asset/images/rentvine-step-12.png';
import rentvineStep13 from '../../../../asset/images/rentvine-step-13.png';
import rentvineStep14 from '../../../../asset/images/rentvine-step-14.png';
import rentvineStep16 from '../../../../asset/images/rentvine-step-16.png';
import rentvineStep17 from '../../../../asset/images/rentvine-step-17.png';
import rentvineStep18 from '../../../../asset/images/rentvine-step-18.png';
import rentvineStep19 from '../../../../asset/images/rentvine-step-19.png';
import rentvineStep20 from '../../../../asset/images/rentvine-step-20.png';

import { Typography } from '@/components/ui/typography';

interface TextSegment {
  text: string;
  bold?: boolean;
}

interface ConfigStep {
  stepNumber: number;
  segments: TextSegment[];
  image?: string;
  imageAlt?: string;
}

const CONFIG_STEPS: ConfigStep[] = [
  {
    stepNumber: 1,
    segments: [{ text: 'Click "Manage integrations"' }],
    image: rentvineStep2,
    imageAlt: 'Manage Integrations',
  },
  {
    stepNumber: 2,
    segments: [{ text: 'Click "Enable"' }],
    image: rentvineStep3,
    imageAlt: 'Enable',
  },
  {
    stepNumber: 3,
    segments: [
      { text: 'In a new tab, login to your ' },
      { text: 'Rentvine account', bold: true },
    ],
  },
  {
    stepNumber: 4,
    segments: [
      { text: 'In ' },
      { text: 'Rentvine', bold: true },
      { text: ', click the User Profile at the top right' },
    ],
    image: rentvineStep5,
    imageAlt: 'User Profile',
  },
  {
    stepNumber: 5,
    segments: [{ text: 'Click "Settings"' }],
    image: rentvineStep6,
    imageAlt: 'Settings',
  },
  {
    stepNumber: 6,
    segments: [{ text: 'Click "Users, Roles and API"' }],
    image: rentvineStep7,
    imageAlt: 'Users Roles and API',
  },
  {
    stepNumber: 7,
    segments: [{ text: 'Click the "API" tab' }],
    image: rentvineStep8,
    imageAlt: 'API Tab',
  },
  {
    stepNumber: 8,
    segments: [{ text: 'Click "New API Key"' }],
    image: rentvineStep9,
    imageAlt: 'New API Key',
  },
  {
    stepNumber: 9,
    segments: [
      { text: 'Enter ' },
      { text: 'PlanOmatic ', bold: true },
      { text: 'in the "Name" field' },
    ],
    image: rentvineStep10,
    imageAlt: 'Name Field',
  },
  {
    stepNumber: 10,
    segments: [{ text: 'Select "Administrator" for the Role' }],
    image: rentvineStep11,
    imageAlt: 'Administrator Role',
  },
  {
    stepNumber: 11,
    segments: [{ text: 'Click "Save"' }],
    image: rentvineStep12,
    imageAlt: 'Save',
  },
  {
    stepNumber: 12,
    segments: [
      { text: 'Copy the ' },
      { text: 'API Key and API Secret ', bold: true },
      { text: 'and paste them in a notes application for easy reference' },
    ],
    image: rentvineStep13,
    imageAlt: 'API Key and Secret',
  },
  {
    stepNumber: 13,
    segments: [{ text: 'Click "Close"' }],
    image: rentvineStep14,
    imageAlt: 'Close',
  },
  {
    stepNumber: 14,
    segments: [
      { text: 'Navigate back to the ' },
      { text: 'PlanOmatic ', bold: true },
      { text: 'application' },
    ],
  },
  {
    stepNumber: 15,
    segments: [
      { text: 'Enter the Rentvine ' },
      { text: 'subdomain', bold: true },
      {
        text: '. This appears between "https://" and "rentvine.com" in your Rentvine URL',
      },
    ],
    image: rentvineStep16,
    imageAlt: 'Rentvine Subdomain',
  },
  {
    stepNumber: 16,
    segments: [
      { text: 'Paste in the ' },
      { text: 'API Key ', bold: true },
      { text: 'that you created in Rentvine' },
    ],
    image: rentvineStep17,
    imageAlt: 'Rentvine API Key',
  },
  {
    stepNumber: 17,
    segments: [
      { text: 'Paste in the ' },
      { text: 'API Secret ', bold: true },
      { text: 'that you created in Rentvine' },
    ],
    image: rentvineStep18,
    imageAlt: 'Rentvine API Secret',
  },
  {
    stepNumber: 18,
    segments: [{ text: 'Click "Test Connection"' }],
    image: rentvineStep19,
    imageAlt: 'Test Connection',
  },
  {
    stepNumber: 19,
    segments: [{ text: 'Click "Save and sync properties"' }],
    image: rentvineStep20,
    imageAlt: 'Save and Sync Properties',
  },
];

interface IntegrationOverviewProps {
  isHowToConfigLinkClicked: boolean;
}

export const IntegrationOverview = ({
  isHowToConfigLinkClicked,
}: IntegrationOverviewProps) => {
  const howToConfigureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHowToConfigLinkClicked) {
      howToConfigureRef.current?.scrollIntoView();
    }
  }, [isHowToConfigLinkClicked]);

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="pl-24 w-[925px] h-[370px]">
          <div className="flex items-center gap-3">
            <div className="p-2">
              <div className="flex flex-col gap-3 pt-8">
                <Typography variant="heading-lg" className="font-medium">
                  Sync your property data to PlanOmatic.
                </Typography>
                <Typography className="text-lg font-medium text-muted-foreground">
                  Track which vacancies already have marketing photos, and place
                  photo orders directly from the PlanOmatic platform with one
                  click.
                </Typography>
              </div>
            </div>
            <div>
              <IconComponent
                src={vendorSyncProperty}
                width={450}
                height={288}
                alt="Vendor Feed"
              />
            </div>
          </div>
        </div>
        <div className="pt-14 w-[900px] h-[370px]">
          <div className="flex items-center gap-3">
            <div className="p-2 pl-20">
              <IconComponent
                src={vendorFeedPhoto}
                width={450}
                height={288}
                alt="Vendor Sync Property"
              />
            </div>
            <div className="ml-auto">
              <div className="flex flex-col gap-3">
                <Typography variant="heading-lg" className="font-medium">
                  Feed your listing media to Rentvine automatically.
                </Typography>
                <Typography className="text-lg font-medium text-muted-foreground">
                  As soon as your order is ready, the listing media will appear
                  in Rentvine with no manual intervention!
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref={howToConfigureRef} className="flex flex-col gap-5 p-8 w-[70%]">
        <Typography variant="heading-2xl" className="font-semibold">
          Instructions to Enable Integration
        </Typography>
        <Typography className="text-lg font-semibold">
          Follow the step-by-step guide below to enable your PlanOmatic &
          Rentvine integration
        </Typography>

        {CONFIG_STEPS.map((step) => (
          <div key={step.stepNumber}>
            <div className="pl-10 -indent-10 before:content-['\2022\00A0\00A0'] before:whitespace-pre">
              <Typography variant="heading-md">
                <span className="font-bold">Step {step.stepNumber}: </span>
                {step.segments.map((segment, i) => (
                  <span
                    key={i}
                    className={segment.bold ? 'font-bold' : 'font-normal'}
                  >
                    {segment.text}
                  </span>
                ))}
              </Typography>
            </div>
            {step.image && (
              <div className="pl-4 mt-3">
                <div className="border border-border rounded overflow-hidden w-[700px] h-[393px]">
                  <IconComponent
                    src={step.image}
                    width={700}
                    height={393}
                    alt={step.imageAlt ?? ''}
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="pl-10 flex items-baseline before:content-['\2022\00A0\00A0'] before:whitespace-pre">
          <Typography variant="heading-md" className="font-bold">
            Watch your properties magically appear!
          </Typography>
        </div>
      </div>
    </>
  );
};
